import { useEffect } from 'react';
import { useCart } from './CartContext';

const formatPrice = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export default function CartDrawer() {
  const { items, isOpen, isCheckingOut, removeItem, updateQuantity, closeCart, checkout, totalItems, totalPrice } = useCart();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <i className="ri-shopping-bag-line text-xl text-stone-800"></i>
            <h2 className="text-lg font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Cart
            </h2>
            {totalItems > 0 && (
              <span className="bg-[#C4956A] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-500"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-4">
                <i className="ri-shopping-bag-line text-2xl text-stone-400"></i>
              </div>
              <p className="text-stone-600 font-semibold mb-1">Your cart is empty</p>
              <p className="text-stone-400 text-sm">Add a scooper or refill bags to get started.</p>
              <button
                onClick={closeCart}
                className="mt-6 px-6 py-2.5 rounded-full border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.variantId} className="flex gap-4 p-4 rounded-2xl bg-[#F8F6F3] border border-stone-100">
                  {/* Image */}
                  {item.imageUrl ? (
                    <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-white">
                      <img src={item.imageUrl} alt={item.productTitle} className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 flex-shrink-0 rounded-xl bg-stone-200 flex items-center justify-center">
                      <i className="ri-shopping-bag-line text-stone-400 text-xl"></i>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-800 font-semibold text-sm leading-snug">{item.productTitle}</p>
                    <p className="text-stone-400 text-xs mt-0.5">{item.variantTitle}</p>
                    <p className="text-[#C4956A] font-bold text-sm mt-1">{formatPrice(item.price * item.quantity)}</p>

                    {/* Quantity controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center border border-stone-200 rounded-full overflow-hidden bg-white">
                        <button
                          onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600 text-sm"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-7 text-center text-stone-800 font-semibold text-xs">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600 text-sm"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.variantId)}
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer text-stone-400 hover:text-red-400"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-stone-100 bg-white">
            {/* Free shipping nudge */}
            {totalPrice < 50 && (
              <div className="flex items-center gap-2 bg-[#F4F7F4] rounded-xl px-4 py-3 mb-4 border border-[#8A9B85]/20">
                <i className="ri-truck-line text-[#8A9B85] text-base flex-shrink-0"></i>
                <p className="text-stone-600 text-xs">
                  Add <strong className="text-[#8A9B85]">{formatPrice(50 - totalPrice)}</strong> more for free shipping!
                </p>
              </div>
            )}
            {totalPrice >= 50 && (
              <div className="flex items-center gap-2 bg-[#F4F7F4] rounded-xl px-4 py-3 mb-4 border border-[#8A9B85]/20">
                <i className="ri-truck-line text-[#8A9B85] text-base flex-shrink-0"></i>
                <p className="text-stone-600 text-xs font-medium text-[#8A9B85]">You qualify for free shipping!</p>
              </div>
            )}

            {/* Order summary */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-stone-500 text-sm">Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'items'})</span>
              <span className="text-stone-800 font-bold text-lg">{formatPrice(totalPrice)}</span>
            </div>

            <button
              onClick={checkout}
              disabled={isCheckingOut}
              className={`w-full py-4 rounded-full font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                isCheckingOut
                  ? 'bg-[#C4956A]/70 text-white cursor-wait'
                  : 'bg-[#C4956A] hover:bg-[#b5845a] text-white'
              }`}
            >
              {isCheckingOut ? (
                <><i className="ri-loader-4-line text-lg animate-spin"></i> Creating Checkout...</>
              ) : (
                <><i className="ri-lock-line text-lg"></i> Checkout — {formatPrice(totalPrice)}</>
              )}
            </button>

            <p className="text-center text-stone-400 text-xs mt-3">
              Taxes and shipping calculated at checkout
            </p>
          </div>
        )}
      </div>
    </>
  );
}
